import React, { useEffect, useState, useCallback } from 'react';
import {
  Radio, Activity, CheckCircle2, Loader2, Circle, Clock,
  RefreshCw, AlertTriangle, FileText, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ACCENT = '#FFD700';

/**
 * Wingman HQ Panel
 * ----------------
 * Live read-only feed of the external Wingman HQ tracker — autonomous
 * wingmen agents, tasks, strategic reports — surfaced inside the Studio's
 * HQ Mission Control. The browser hits our backend proxy `/api/wingman/*`
 * which injects the Bearer key server-side. No upstream secret ever
 * reaches the browser.
 */
export default function WingmanHQPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tasks'); // 'tasks' | 'reports'

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await fetch(`${API}/api/wingman/overview`, { credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Wingman HQ returned ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) return <PanelShell loading />;
  if (err && !data) return <PanelShell err={err} onRetry={load} />;
  if (!data) return null;

  const { status, wingmen, task_counts, tasks, reports } = data;

  return (
    <div data-testid="wingman-hq-panel" style={{
      border: '1px solid rgba(15,15,30,0.08)',
      background: '#FFFFFF',
      boxShadow: '0 1px 2px rgba(15,15,30,0.03), 0 6px 16px rgba(15,15,30,0.04)',
      margin: '14px 22px 0',
      overflow: 'hidden',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {/* Header strip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
        background: 'linear-gradient(135deg, rgba(255,215,0,0.06), rgba(255,215,0,0.01))',
        borderBottom: '1px solid rgba(15,15,30,0.05)',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', fontSize: 9, fontWeight: 700,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: ACCENT, background: 'rgba(255,215,0,0.1)',
          border: '1px solid rgba(255,215,0,0.32)',
        }}>
          <Radio size={11} /> Wingman HQ · Live
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#0F0F1A' }}>
          {status.key_name || 'Tracker'}
        </span>
        <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.5)' }}>
          {status.connected ? 'connected' : 'offline'} · {status.usage_count} calls
        </span>

        {/* Counter pills */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <Pill icon={Circle} label="To do" value={task_counts.todo} color="rgba(15,15,30,0.55)" />
          <Pill icon={Loader2} label="Running" value={task_counts.in_progress} color="#06B6D4" spin />
          <Pill icon={CheckCircle2} label="Done" value={task_counts.done} color="#22C55E" />
          <Pill icon={Clock} label="Min" value={task_counts.total_time_min} color="rgba(15,15,30,0.55)" mono />

          <button
            onClick={load}
            data-testid="wingman-refresh-btn"
            title="Refresh"
            style={{
              padding: 6, background: 'transparent',
              border: '1px solid rgba(15,15,30,0.1)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center',
              color: 'rgba(15,15,30,0.55)',
            }}
          >
            <RefreshCw size={11} className={loading ? 'spin-anim' : ''} />
          </button>
        </div>
      </div>

      {/* Wingmen roster */}
      {wingmen?.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(15,15,30,0.05)',
          background: 'rgba(15,15,30,0.012)',
        }}>
          {wingmen.map(w => <WingmanChip key={w.id} w={w} />)}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '1px solid rgba(15,15,30,0.05)',
      }}>
        <Tab id="tasks" active={tab} onClick={setTab} icon={Activity}>
          Recent tasks · {tasks.length}
        </Tab>
        <Tab id="reports" active={tab} onClick={setTab} icon={FileText}>
          Strategic reports · {reports.length}
        </Tab>
      </div>

      {/* Content */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {tab === 'tasks' ? (
          tasks.length === 0
            ? <Empty label="No tasks yet" />
            : tasks.slice(0, 14).map(t => <TaskRow key={t.id} t={t} />)
        ) : (
          reports.length === 0
            ? <Empty label="No reports yet" />
            : reports.slice(0, 6).map(r => <ReportRow key={r.id} r={r} />)
        )}
      </div>
    </div>
  );
}

/* ============== sub-components ============== */

function PanelShell({ loading, err, onRetry }) {
  return (
    <div data-testid="wingman-hq-panel" style={{
      margin: '14px 22px 0', padding: '14px 16px',
      border: '1px solid rgba(15,15,30,0.08)', background: '#FFFFFF',
      display: 'flex', alignItems: 'center', gap: 12,
      fontSize: 11.5, color: 'rgba(15,15,30,0.55)',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 8px', fontSize: 9, fontWeight: 700,
        letterSpacing: '0.16em', textTransform: 'uppercase',
        color: ACCENT, background: 'rgba(255,215,0,0.1)',
        border: '1px solid rgba(255,215,0,0.32)',
      }}>
        <Radio size={11} /> Wingman HQ
      </span>
      {loading && (
        <>
          <Loader2 size={12} className="spin-anim" /> Connecting to upstream tracker…
        </>
      )}
      {err && (
        <>
          <AlertTriangle size={12} color="#B91C1C" />
          <span style={{ color: '#B91C1C' }}>{err}</span>
          {onRetry && (
            <button
              onClick={onRetry}
              data-testid="wingman-retry-btn"
              style={{
                marginLeft: 'auto', padding: '4px 10px',
                fontSize: 10, fontFamily: 'inherit',
                background: 'transparent', border: '1px solid rgba(15,15,30,0.15)',
                cursor: 'pointer', color: '#0F0F1A',
              }}
            >Retry</button>
          )}
        </>
      )}
    </div>
  );
}

function Pill({ icon: Icon, label, value, color, spin, mono }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 8px',
      border: `1px solid ${color}33`,
      background: `${color}0E`,
      color: color,
      fontSize: 10, fontWeight: 700,
    }}>
      <Icon size={10} className={spin ? 'spin-anim' : ''} />
      <span style={{
        color: '#0F0F1A',
        fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</span>
      <span style={{ color: 'rgba(15,15,30,0.5)', fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function WingmanChip({ w }) {
  const ringColor = colorFor(w.color);
  const initials = (w.name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div title={`${w.name} · ${w.role || ''}${w.skills?.length ? ` · ${w.skills.slice(0,3).join(', ')}` : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '4px 10px 4px 4px',
        border: `1px solid ${ringColor}33`,
        background: '#FFFFFF',
      }}>
      <span style={{
        width: 22, height: 22,
        background: `${ringColor}1A`,
        color: ringColor,
        border: `1px solid ${ringColor}55`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 800,
      }}>{initials}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#0F0F1A' }}>{w.name}</span>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: w.status === 'active' ? '#22C55E' : 'rgba(15,15,30,0.25)',
      }} />
    </div>
  );
}

function Tab({ id, active, onClick, icon: Icon, children }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`wingman-tab-${id}`}
      style={{
        flex: 1, padding: '9px 14px',
        background: isActive ? '#FFFFFF' : 'rgba(15,15,30,0.018)',
        border: 'none', borderBottom: `2px solid ${isActive ? ACCENT : 'transparent'}`,
        cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
        color: isActive ? '#0F0F1A' : 'rgba(15,15,30,0.55)',
        display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center',
        transition: 'all 0.12s',
      }}
    >
      <Icon size={11} /> {children}
    </button>
  );
}

function TaskRow({ t }) {
  const stat = TASK_STATUS[t.status] || TASK_STATUS.todo;
  return (
    <div data-testid={`wingman-task-${t.id}`} style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '9px 16px',
      borderBottom: '1px solid rgba(15,15,30,0.04)',
      fontSize: 11.5, color: '#0F0F1A',
    }}>
      <stat.Icon size={12} color={stat.color}
        className={t.status === 'in_progress' ? 'spin-anim' : ''}
        style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 700, marginBottom: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{t.title}</div>
        {t.description && (
          <div style={{
            fontSize: 10.5, color: 'rgba(15,15,30,0.58)', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>{t.description}</div>
        )}
      </div>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 9, fontWeight: 700, color: stat.color,
          padding: '1px 6px', background: `${stat.color}14`,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>{stat.label}</span>
        <span style={{ fontSize: 9, color: 'rgba(15,15,30,0.45)', fontVariantNumeric: 'tabular-nums' }}>
          {formatRelative(t.updated_at || t.created_at)}
          {t.time_spent_min ? ` · ${t.time_spent_min}m` : ''}
        </span>
      </div>
    </div>
  );
}

function ReportRow({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`wingman-report-${r.id}`} style={{
      borderBottom: '1px solid rgba(15,15,30,0.04)',
      fontSize: 11.5, color: '#0F0F1A',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '9px 16px', cursor: 'pointer',
          background: 'transparent', border: 'none', fontFamily: 'inherit',
          color: 'inherit',
        }}
      >
        <ChevronRight size={11} style={{
          marginTop: 3, flexShrink: 0,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s', color: 'rgba(15,15,30,0.4)',
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: 11.5,
            display: '-webkit-box', WebkitLineClamp: open ? 4 : 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', lineHeight: 1.55,
          }}>{r.summary || '(no summary)'}</div>
          <div style={{
            display: 'flex', gap: 10, marginTop: 4, fontSize: 9.5, color: 'rgba(15,15,30,0.48)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            <span>{formatRelative(r.created_at)}</span>
            {r.activities?.length > 0 && <span>· {r.activities.length} activities</span>}
            {r.blockers?.length > 0 && <span style={{ color: '#B91C1C' }}>· {r.blockers.length} blocker{r.blockers.length > 1 ? 's' : ''}</span>}
            {r.next_steps?.length > 0 && <span>· {r.next_steps.length} next steps</span>}
          </div>
        </div>
      </button>
      {open && (
        <div style={{
          padding: '4px 16px 12px 37px',
          fontSize: 10.5, color: 'rgba(15,15,30,0.7)', lineHeight: 1.55,
        }}>
          {r.next_steps?.length > 0 && (
            <Section title="Next steps">
              {r.next_steps.map((s, i) => <li key={i}>{s}</li>)}
            </Section>
          )}
          {r.blockers?.length > 0 && (
            <Section title="Blockers" tone="warn">
              {r.blockers.map((s, i) => <li key={i}>{s}</li>)}
            </Section>
          )}
          {r.activities?.length > 0 && (
            <Section title="Activity">
              {r.activities.slice(0, 8).map((a, i) => (
                <li key={i}>
                  <span style={{ color: 'rgba(15,15,30,0.5)' }}>{a.type || 'note'}: </span>
                  {a.description}
                </li>
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, tone, children }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: tone === 'warn' ? '#B91C1C' : 'rgba(15,15,30,0.5)',
        marginBottom: 3,
      }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 16, listStyle: 'disc' }}>{children}</ul>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div style={{
      padding: '24px 16px', textAlign: 'center',
      fontSize: 11.5, color: 'rgba(15,15,30,0.42)',
    }}>{label}</div>
  );
}

/* ============== utils ============== */

const TASK_STATUS = {
  todo:        { label: 'TO DO',    color: 'rgba(15,15,30,0.55)', Icon: Circle },
  in_progress: { label: 'RUNNING',  color: '#06B6D4',               Icon: Loader2 },
  done:        { label: 'DONE',     color: '#22C55E',               Icon: CheckCircle2 },
  blocked:     { label: 'BLOCKED',  color: '#B91C1C',               Icon: AlertTriangle },
};

function colorFor(name) {
  switch ((name || '').toLowerCase()) {
    case 'blue':   return '#3B82F6';
    case 'red':    return '#EF4444';
    case 'green':  return '#22C55E';
    case 'gold':
    case 'yellow': return '#FFD700';
    case 'purple': return '#A855F7';
    case 'cyan':   return '#06B6D4';
    case 'pink':   return '#EC4899';
    case 'orange': return '#F97316';
    default:       return '#0F0F1A';
  }
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ''; }
}
